import type { PlatformTagBoard } from '@brush/api-client';

/** Each reason the API can give, worded where the board is named. */
const REASON: Record<string, string> = {
  scope: 'scopes on this tag',
  points_system: 'scores by this tag through its selected points system',
};

/**
 * The sentence an administrator reads after removing a platform tag that a Draft board depends on,
 * or null when none did — the ordinary answer.
 *
 * A running board depending on the tag refuses the removal outright (the API's 409 names each one
 * and why), since its standings would move under a room that did not choose it. A Draft publishes
 * nothing, so it does not block — but the tag's removal still changes it, in one of two ways, and the
 * administrator who just removed it is the only person who knows:
 *
 * - its **scope** named the tag, so it now counts fewer events — none through that tag;
 * - its **points system** scores by the tag (a v2 tag multiplier or bonus selector), so the events it
 *   counts are worth something different.
 *
 * Each board is named with its room and its reason, so there is somebody to tell and something to
 * tell them; a platform template belongs to no room and is called one.
 */
export function draftBoardWarning(tag: string, boards: readonly PlatformTagBoard[]): string | null {
  if (boards.length === 0) return null;

  const named = boards.map((b) => {
    const where = b.roomName ? `in ${b.roomName}` : '(a platform template)';
    const why = b.reasons.map((r) => REASON[r] ?? r).join(', and ');
    return `${b.kind} “${b.name}” ${where} (${why})`;
  });

  const which = boards.length === 1 ? 'A draft board depends' : 'These draft boards depend';

  const consequences: string[] = [];
  if (boards.some((b) => b.reasons.includes('scope'))) {
    consequences.push('a scope naming it will now match nothing through that tag');
  }
  if (boards.some((b) => b.reasons.includes('points_system'))) {
    consequences.push('a points system scoring by it will score those events without it');
  }

  return (
    `${which} on “${tag}”: ${named.join('; ')}. From now on ${consequences.join(', and ')} — ` +
    `worth telling the room before the board is started.`
  );
}
